import {
  LedgerType,
  isReceiveType,
} from "@syla/shared/types/models/LedgerBase";
import {
  TransactionTagType,
  TransactionType,
} from "@syla/shared/types/models/TransactionBase";
import { orderBy } from "lodash";
import React, { useMemo } from "react";
import { getTransactionIconAndText } from "../../../../../helper/transaction/getTransactionIconAndText";
import { useCurrentAccountStore } from "../../../../../store/currentAccountStore";
import { useQueryGetAccountTransactionTypes } from "../../../../../store/useQueryGetAccountTransactionTypes";
import { StandardSelectOption } from "../../../../selector/parts/SelectOption";
import { Selector, SelectorProps } from "../../../../selector/Selector";

type LedgerTypeFilterProps = {
  values: TransactionTagType[];
  onChange: (values: TransactionTagType[]) => void;
} & Omit<
  SelectorProps<TransactionTagType>,
  "onChange" | "options" | "values" | "isLoading" | "placeholder"
>;

export function LedgerTypeFilter({
  values,
  onChange,
  ...props
}: LedgerTypeFilterProps): React.ReactElement {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const queryAccountTransactionTypes =
    useQueryGetAccountTransactionTypes(accountId);

  const ledgerTypeOptions:
    | StandardSelectOption<TransactionTagType>[]
    | undefined = useMemo(() => {
    if (!queryAccountTransactionTypes.data) {
      return undefined;
    }

    const ordered = orderBy(queryAccountTransactionTypes.data, [
      // sort according to TYPE_FILTER_ORDER
      (type) => {
        const index = TYPE_FILTER_ORDER.indexOf(type);
        return index === -1 ? 99 : index;
      },
      // then put Receive types before Send and other
      (type) => (isReceiveType({ type: type as LedgerType }) ? 0 : 1),
    ]);

    return ordered.map((type) => {
      const { icon, text } = getTransactionIconAndText({ type });
      return {
        value: type,
        keywords: [text],
        data: {
          label: text,
          icon,
        },
      };
    });
  }, [queryAccountTransactionTypes.data]);

  return (
    <Selector
      values={values}
      onChange={onChange}
      options={ledgerTypeOptions ?? EMPTY}
      isLoading={ledgerTypeOptions === undefined}
      placeholder="Type"
      enableSearch
      enableBulkSelect
      enableDeselect
      enableMultiSelect
      moveSelectedToTop
      {...props}
    />
  );
}

const EMPTY = [];

const TYPE_FILTER_ORDER: TransactionTagType[] = [
  LedgerType.Deposit,
  LedgerType.Withdrawal,
  LedgerType.TransferIn,
  LedgerType.TransferOut,
  TransactionType.Trade,
  TransactionType.Wrap,
  TransactionType.Mint,
  TransactionType.Rebase,
  TransactionType.RemoveLiquidity,
  TransactionType.AddLiquidity,
];
